import React, { useRef, useEffect } from "react"
import styled from "@emotion/styled"
import lottie from "lottie-web"
import Section from "src/components/Section"
import Grid from "src/components/Grid"
import ScrollEntrance from "src/components/ScrollEntrance"
import TextLockup from "src/components/TextLockup"
import { colors, typography, util, globals, mq } from "src/styles"
import { Card } from "src/styles/globals"
import step1animation from "./section1illo.json"
import step2animation from "./section2illo.json"
import step3animation from "./section3illo.json"

const Wrapper = styled(Section)`
  padding: 0;
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
  `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => (hideMobile && "display: none")}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => (hideDesktop && "display: none")}
  }
`

const IntroText = styled(TextLockup)`
  ${globals.verticalSpacing("padding-top")}
  ${util.responsiveStyles(
    "padding-bottom",
    70,
    50,
    60,
    52
  )}
  h1, h2, h3, h4, h5, h6, p {
    margin-left: auto;
    margin-right: auto;
  }
  h1,
  h2,
  h3 {
    max-width: 9em;
  }
  h4,
  h4 {
    max-width: 12em;
  }
  h6 {
    max-width: 14em;
  }
  p {
    max-width: 32em;
    color: ${colors.lightTextColor};
    strong {
      color: ${colors.textColor};
    }
  }
`

const Dot = styled.span`
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ color }) => color};
  position: absolute;
  top: 20px;
  right: 20px;
`

const SectionContent = styled(Card)`
  padding: 20px 20px 24px;
  position: relative;
  height: 100%;
  min-height: 240px;
  ${util.responsiveStyles("min-height", 350, 240, 220, 216)}
  background: ${colors.extraLightGrey};
  display: flex;
  .eyebrow {
    margin-top: -.2em;
    ${typography.h5}
    max-width: 12em;
    margin-bottom: .5em;
    margin-right: 35px;
    color: ${colors.textColor};
  }
  p {
    max-width: 20em;
    color: ${colors.lightTextColor};
  }
`

const CardText = styled(TextLockup)`
  display: flex !important;
  flex: 1;
  direction: ltr;
  padding-right: 30px;
  h5 {
    max-width: 15em;
  }
  p {
    margin-bottom: 0;
  }
  > div {
    display: flex;
    flex: 1;
    & > div {
      height: 100%;
      display: flex;
      flex: 1;
      flex-direction: column;
      justify-content: space-between;
    }
  }
`

const SectionGrid = styled(Grid)`
  ${mq.mediumAndUp} {
    ${({ index }) =>
      index === 0
        ? `
      margin-top: -5%;
      margin-bottom: -10%;
    `
        : ""}
  }
`

const MediaWrapper = styled.div`
  ${mq.smallAndBelow} {
    display: none;
  }
`

const HowItWorks = ({
  className,
  nextTheme,
  prevTheme,
  theme,
  spacing,
  isFirstSection,
  introText,
  stepOne,
  stepTwo,
  stepThree,
  id,
  hideMobile,
  hideDesktop,
}) => {
  const animationContainer1 = useRef(null)
  const animationContainer2 = useRef(null)
  const animationContainer3 = useRef(null)

  useEffect(() => {
    const anim = (ref, animation) =>
      lottie.loadAnimation({
        container: ref.current,
        renderer: "svg",
        loop: true,
        autoplay: true,
        animationData: animation,
      })
    anim(animationContainer1, step1animation)
    anim(animationContainer2, step2animation)
    anim(animationContainer3, step3animation)
    // return () => anim.destroy()
  }, [])

  const refs = [animationContainer1, animationContainer2, animationContainer3]

  const renderSection = (section, index, color) => (
    <div>
      <SectionGrid
        small="container"
        medium={index === 0 ? "m [1] m [1]" : "m [1] m [1] m"}
        large={index === 0 ? "2 [8] 1 [13]" : "2 [8] 1 [12] 1"}
        // larger='4 [6] 1 [11] 2'
        larger={index === 0 ? "4 [6] 1 [13]" : "4 [6] 1 [11] 2"}
        vAlign="center"
        gridDirection={index % 2 ? "rtl" : "ltr"}
        as={ScrollEntrance}
        index={index}
      >
        <div>
          <SectionContent>
            <Dot color={color} />
            <CardText
              transitionIn={false}
              headline={section.title}
              headlineSize="h5"
              headlineElement="h5"
              text={section.text}
            />
          </SectionContent>
        </div>
        {/* for lottie animation */}
        <MediaWrapper>
          <div ref={refs[index]} />
        </MediaWrapper>
      </SectionGrid>
    </div>
  )

  return (
    <Wrapper
      className={className}
      prevTheme={prevTheme}
      setTheme={theme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      vertical={spacing?.vertical}
      horizontal={spacing?.horizontal}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      <Grid small="container" medium="1 [10] 1" vAlign="top">
        <div>
          <IntroText
            alignment="center"
            eyebrow={introText?.eyebrow}
            text={introText?._rawText}
          />
        </div>
      </Grid>
      <Grid small="[1]" rowGap="20px">
        {renderSection(stepOne, 0, colors.red)}
        {renderSection(stepTwo, 1, colors.yellow)}
        {renderSection(stepThree, 2, colors.green)}
      </Grid>
    </Wrapper>
  )
}

export default HowItWorks
